import { CatalogItemType } from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { CatalogFieldMapping } from '@pushly/models/lib/structs/catalogs/catalog-field-mapping'
import { CatalogExpectedType } from '@pushly/models/lib/enums/catalogs/catalog-expected-type'

export const CATALOG_DEFAULT_SCHEMA_TYPES: Record<CatalogItemType, string> = {
    [CatalogItemType.RECIPE]: 'Recipe',
    [CatalogItemType.PRODUCT]: 'Product',
    [CatalogItemType.TICKETED_EVENT]: 'Event',
}

/**
 * Default field mappings matching the treasure-chest DDB model fields.
 * Recipe fields mirror Recipe.default_field_mappings in treasure-chest.
 * Product fields mirror the Product DDB model.
 * TicketedEvent fields mirror the TicketedEvent DDB model.
 */
export const CATALOG_DEFAULT_MAPPINGS: Record<CatalogItemType, CatalogFieldMapping[]> = {
    [CatalogItemType.RECIPE]: [
        { catalog_field: 'item_id', json_path: '$.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'name', json_path: '$.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'url', json_path: '$.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'description', json_path: '$.description', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'image', json_path: '$.image.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'author', json_path: '$.author.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'recipe_category', json_path: '$.recipeCategory', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'recipe_cuisine', json_path: '$.recipeCuisine', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'ingredients', json_path: '$.recipeIngredient', expected_type: CatalogExpectedType.LIST },
        { catalog_field: 'instructions', json_path: '$.recipeInstructions', expected_type: CatalogExpectedType.LIST },
        { catalog_field: 'prep_time', json_path: '$.prepTime', expected_type: CatalogExpectedType.DURATION_SECONDS },
        { catalog_field: 'cook_time', json_path: '$.cookTime', expected_type: CatalogExpectedType.DURATION_SECONDS },
        { catalog_field: 'total_time', json_path: '$.totalTime', expected_type: CatalogExpectedType.DURATION_SECONDS },
        { catalog_field: 'recipe_yield', json_path: '$.recipeYield', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'keywords', json_path: '$.keywords', expected_type: CatalogExpectedType.LIST },
        { catalog_field: 'nutrition', json_path: '$.nutrition', expected_type: CatalogExpectedType.DICT },
        { catalog_field: 'aggregate_rating', json_path: '$.aggregateRating.ratingValue', expected_type: CatalogExpectedType.FLOAT },
    ] as CatalogFieldMapping[],
    [CatalogItemType.PRODUCT]: [
        { catalog_field: 'item_id', json_path: '$.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'name', json_path: '$.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'url', json_path: '$.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'description', json_path: '$.description', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'image', json_path: '$.image', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'brand_name', json_path: '$.brand.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'price', json_path: '$.offers.price', expected_type: CatalogExpectedType.FLOAT },
        { catalog_field: 'price_currency', json_path: '$.offers.priceCurrency', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'sale_price', json_path: '$.offers.lowPrice', expected_type: CatalogExpectedType.FLOAT },
        { catalog_field: 'availability', json_path: '$.offers.availability', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'color', json_path: '$.color', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'material', json_path: '$.material', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'condition', json_path: '$.itemCondition', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'item_group_id', json_path: '$.isVariantOf.productGroupID', expected_type: CatalogExpectedType.STR },
    ] as CatalogFieldMapping[],
    [CatalogItemType.TICKETED_EVENT]: [
        { catalog_field: 'item_id', json_path: '$.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'name', json_path: '$.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'url', json_path: '$.url', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'description', json_path: '$.description', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'image', json_path: '$.image', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'date_start', json_path: '$.startDate', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'date_end', json_path: '$.endDate', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'price', json_path: '$.offers.price', expected_type: CatalogExpectedType.FLOAT },
        { catalog_field: 'price_currency', json_path: '$.offers.priceCurrency', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'location_name', json_path: '$.location.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'location_city', json_path: '$.location.address.addressLocality', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'location_region', json_path: '$.location.address.addressRegion', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'location_country', json_path: '$.location.address.addressCountry', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'performer_name', json_path: '$.performer.name', expected_type: CatalogExpectedType.STR },
        { catalog_field: 'status', json_path: '$.eventStatus', expected_type: CatalogExpectedType.STR },
    ] as CatalogFieldMapping[],
}

export const REQUIRED_CATALOG_FIELDS = ['item_id', 'name', 'url']

export const PRODUCT_SCHEMA_SUBTYPES = [
    'Product',
    'IndividualProduct',
    'ProductCollection',
    'ProductGroup',
    'ProductModel',
    'SomeProducts',
    'Vehicle',
]

export const EVENT_SCHEMA_SUBTYPES = [
    'Event',
    'BusinessEvent',
    'ChildrensEvent',
    'ComedyEvent',
    'ConferenceEvent',
    'CourseInstance',
    'DanceEvent',
    'DeliveryEvent',
    'EducationEvent',
    'EventSeries',
    'ExhibitionEvent',
    'Festival',
    'FoodEvent',
    'Hackathon',
    'LiteraryEvent',
    'MusicEvent',
    'PerformingArtsEvent',
    'PublicationEvent',
    'SaleEvent',
    'ScreeningEvent',
    'SocialEvent',
    'SportsEvent',
    'TheaterEvent',
    'VisualArtsEvent',
]
